import {
  ThreeToolsInstance,
  usePartDefinition,
  useProductStoreContext,
} from "@project/emerge";
import { markProductSceneChanged } from "@project/emerge/src/new/sceneActivity";
import { compactGeometry } from "@project/emerge/src/util/compactGeometry";
import { applyCreasedNormals } from "@project/emerge/src/util/creasedNormals";
import type { Sides } from "@project/emerge-types";
import * as MakerJS from "makerjs";
import { memo, useLayoutEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import {
  computeCutoutDxDy,
  getCachedSvgModel,
  transformCutoutModel,
} from "../geometry/cutoutPanelPlane";
import { panelGeometryCache } from "../geometry/FinishedGeometryCache";
import type { ExtendedCutoutDefinition } from "../interfaces/CutoutProperties";
import type { PanelColor } from "../interfaces/PanelColor";
import type { PanelDefinition } from "../interfaces/PanelProperties";
import type { TrayPanelDefinition } from "../interfaces/TrayPanelProperties";
import { PanelMaterial } from "../materials/PanelMaterial";
import { RectPropertiesBySide } from "../structure/RectProperties";
import { PartEdges } from "./PartEdges";
import { useLatestCommittedResource } from "./useLatestCommittedResource";

/**
 * Stable value-based keys for geometry memoization.
 *
 * Each product build clones preserved parts (cloneDeep when part interactions
 * exist), so `position`/`size`/`cutouts` get fresh object references every
 * build even when their values are unchanged. Keying the geometry `useMemo` on
 * object identity would therefore re-run the (expensive) extrusion on every
 * build, including during an unrelated part drag. Keying on these value strings
 * skips the rebuild when the panel's geometry inputs did not actually change.
 */
export function vec3Key(v: THREE.Vector3 | undefined): string {
  return v ? `${v.x},${v.y},${v.z}` : "";
}

export function vec2Key(v: THREE.Vector2 | undefined): string {
  return v ? `${v.x},${v.y}` : "";
}

type PanelGeometryFields = {
  size: THREE.Vector2;
  position: THREE.Vector3;
  side: Sides;
};

/** Read panel placement fields from store part without intersection-typed hooks. */
function readPanelGeometryFields(part: unknown): PanelGeometryFields | null {
  if (!part || typeof part !== "object") {
    return null;
  }
  if (!("size" in part) || !("position" in part) || !("side" in part)) {
    return null;
  }
  const { size, position, side } = part as PanelGeometryFields;
  if (!size || !position || side === undefined) {
    return null;
  }
  return { size, position, side };
}

/** Identity shared by value keys and duplicate filtering. */
function cutoutValueKey(cutout: ExtendedCutoutDefinition): string {
  const inv = (cutout as { invert?: { x?: boolean; y?: boolean } }).invert;
  const path =
    typeof cutout.path === "string" ? cutout.path : JSON.stringify(cutout.path);
  return `${cutout.position.x},${cutout.position.y},${cutout.position.z}|${path}|${cutout.orientation || 0}|${inv?.x ? "x" : ""}${inv?.y ? "y" : ""}`;
}

/** Canonical string of every cutout field that affects the extruded geometry. */
export function cutoutsValueKey(
  cutouts: ExtendedCutoutDefinition[] | undefined,
): string {
  return cutouts?.map(cutoutValueKey).join(";") ?? "";
}

/** Key the finished geometry in panel space, independent of placement/colour. */
export function panelGeometryKey(
  size: THREE.Vector2,
  position: THREE.Vector3,
  side: Sides,
  cutouts: ExtendedCutoutDefinition[],
  strength: number,
): string {
  const rectProps = RectPropertiesBySide[side];
  const localCutouts = rectProps
    ? cutouts.flatMap((cutout) => {
        if (!cutout.path || typeof cutout.path !== "string") return [];
        const { dx, dy } = computeCutoutDxDy(
          cutout.position,
          size,
          position,
          rectProps,
        );
        const invert = (cutout as { invert?: { x?: boolean; y?: boolean } })
          .invert;
        return [
          [
            cutout.path,
            dx,
            dy,
            cutout.orientation || 0,
            !!invert?.x,
            !!invert?.y,
          ],
        ];
      })
    : [];
  return JSON.stringify([size.x, size.y, strength, localCutouts]);
}

/**
 * Filtert doppelte Cutouts aus der Liste der Cutouts.
 * Duplikate haben dieselbe Position, Form, Orientierung und Spiegelung.
 * @param cutouts Liste der Cutouts
 * @returns Gefilterte Liste ohne Duplikate
 */
export function filterDuplicateCutouts(cutouts: ExtendedCutoutDefinition[]): {
  uniqueCutouts: ExtendedCutoutDefinition[];
  duplicates: Array<{ index: number; duplicateOfIndex: number }>;
} {
  if (!cutouts || cutouts.length === 0)
    return { uniqueCutouts: [], duplicates: [] };

  const uniqueCutouts: ExtendedCutoutDefinition[] = [];
  const duplicates: Array<{ index: number; duplicateOfIndex: number }> = [];
  const seenCutouts = new Map<string, number>();

  for (const [index, cutout] of cutouts.entries()) {
    const uniqueKey = cutoutValueKey(cutout);

    // Wenn wir diesen Schlüssel noch nicht gesehen haben, füge das Cutout zur Liste hinzu
    const existingIndex = seenCutouts.get(uniqueKey);
    if (existingIndex !== undefined) {
      // Speichere Duplikat-Information (aktueller Index und Index des Originals)
      duplicates.push({
        index,
        duplicateOfIndex: existingIndex,
      });
    } else {
      seenCutouts.set(uniqueKey, index);
      uniqueCutouts.push(cutout);
    }
  }

  return { uniqueCutouts, duplicates };
}

/**
 * Verarbeitung von SVG-Pfaden in Cutouts:
 * - Wenn ein SVG-String mehrere separate Pfade enthält (getrennt durch 'Z'),
 *   wird jeder Pfad als eigenständige Geometrie behandelt.
 * - Jede Geometrie wird als Loch im Panel-Rechteck hinzugefügt und dann extrudiert.
 * - Für Debugging werden die Pfade analysiert und visualisiert.
 */

/** Convert cutout chains to Three.js hole shapes with correct winding */
function convertChainsToHoles(cutoutModel: MakerJS.IModel): THREE.Shape[] {
  const holes: THREE.Shape[] = [];
  const chains = MakerJS.model.findChains(cutoutModel) as
    | MakerJS.IChain[]
    | undefined;
  if (!chains) return holes;

  for (const chain of chains) {
    const holeShape = ThreeToolsInstance.Chain2Shape(chain);
    const points = holeShape.getPoints();

    // Ensure hole has reverse winding (CW) relative to panel (CCW)
    if (THREE.ShapeUtils.isClockWise(points)) {
      holes.push(holeShape);
    } else {
      points.reverse();
      holes.push(new THREE.Shape(points));
    }
  }
  return holes;
}

/**
 * Chamfer along the panel outline, in millimetres.
 *
 * A perfectly sharp edge catches no highlight and is the second-strongest
 * "rendered, not photographed" tell after a black outline. `bevelOffset` pulls
 * the chamfer inwards and the depth is reduced by the two chamfer faces, so the
 * panel keeps its exact outer dimensions — these are manufacturing figures.
 */
const PANEL_BEVEL = 0.3;

/**
 * A plain rectangular panel, chamfered like every other one.
 *
 * The three paths that used to return a bare `BoxGeometry` — no cutouts, no
 * rect properties, extrusion failure — left those panels with razor-sharp
 * edges while their neighbours were chamfered, which reads as an inconsistency
 * within one product rather than as a style.
 */
export function createPlainPanelGeometry(
  size: THREE.Vector2,
  strength: number,
): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.lineTo(size.x, 0);
  shape.lineTo(size.x, size.y);
  shape.lineTo(0, size.y);
  shape.closePath();

  return createExtrudedGeometry(shape, size, strength);
}

/** Create extruded geometry from a shape with proper UV mapping */
function createExtrudedGeometry(
  panelShape: THREE.Shape,
  size: THREE.Vector2,
  strength: number,
): THREE.BufferGeometry {
  // Panels thinner than two chamfers keep square edges rather than collapse.
  const bevel = strength > PANEL_BEVEL * 4 ? PANEL_BEVEL : 0;
  const extrudeSettings = {
    depth: strength - bevel * 2,
    bevelEnabled: bevel > 0,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelOffset: -bevel,
    bevelSegments: 1,
    steps: 1,
    curveSegments: 12,
  };

  const geometry = new THREE.ExtrudeGeometry(panelShape, extrudeSettings);
  // Extrusion spans z from -bevel to (strength - bevel); centre it on 0.
  geometry.translate(-size.x / 2, -size.y / 2, bevel - strength / 2);

  // `curveSegments` above turns every arc in the outline — a rounded corner, a
  // circular cutout for a dish or a handle — into a dozen flat strips. Creasing
  // shades them as the cylinder they stand for while leaving the panel's own
  // square edges and its chamfers hard. Non-indexed geometry in, same instance
  // back, so the UVs written below still line up.
  applyCreasedNormals(geometry);

  // Manually set UV coordinates based on actual panel size
  // This ensures consistent texture mapping regardless of cutouts
  // The geometry is translated so it's centered: x from -size.x/2 to +size.x/2, y from -size.y/2 to +size.y/2
  const positions = geometry.attributes.position;
  if (!positions) {
    return geometry;
  }

  const uvs = new Float32Array(positions.count * 2);

  // Get the existing UV attribute to preserve face information
  const existingUVs = geometry.attributes.uv;

  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);

    // Determine which face this vertex belongs to by checking which coordinate is at the boundary
    const tolerance = 0.01;
    const atTopZ = Math.abs(z - strength / 2) < tolerance;
    const atBottomZ = Math.abs(z + strength / 2) < tolerance;

    if (atTopZ || atBottomZ) {
      // Top or bottom face (main panel surface) - map x,y directly to UV
      // This is the most important face for texture mapping
      uvs[i * 2] = (x + size.x / 2) / size.x;
      uvs[i * 2 + 1] = (y + size.y / 2) / size.y;
    } else if (existingUVs) {
      // Side face - use the existing UV mapping but scale it to panel dimensions
      const baseU = existingUVs.getX(i);
      const baseV = existingUVs.getY(i);
      uvs[i * 2] = baseU;
      uvs[i * 2 + 1] = baseV;
    } else {
      // Side face fallback: map based on position
      uvs[i * 2] = (x + size.x / 2) / size.x;
      uvs[i * 2 + 1] = (z + strength / 2) / strength;
    }
  }

  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));

  return compactGeometry(geometry);
}

/** Transformed cutout with precomputed model and internal flag */
interface TransformedCutout {
  cutout: ExtendedCutoutDefinition;
  model: MakerJS.IModel;
  isInternal: boolean;
  hash: string;
  dx: number;
  dy: number;
}

/** Transform and classify a cutout */
function transformAndClassifyCutout(
  cutout: ExtendedCutoutDefinition,
  size: THREE.Vector2,
  position: THREE.Vector3,
  rectProps: (typeof RectPropertiesBySide)[Sides],
): TransformedCutout | null {
  if (!cutout.path || typeof cutout.path !== "string") return null;

  const { dx, dy } = computeCutoutDxDy(
    {
      x: cutout.position.x,
      y: cutout.position.y,
      z: cutout.position.z,
    },
    size,
    position,
    rectProps,
  );

  let cachedModel = getCachedSvgModel(cutout.path);
  const inv = (cutout as { invert?: { x?: boolean; y?: boolean } }).invert;
  if (inv?.x) {
    cachedModel = MakerJS.model.mirror(cachedModel, true, false);
  }
  if (inv?.y) {
    cachedModel = MakerJS.model.mirror(cachedModel, false, true);
  }
  const model = transformCutoutModel(
    cachedModel,
    dx,
    dy,
    cutout.orientation || 0,
  );

  // Check if fully inside panel bounds
  const measure = MakerJS.measure.modelExtents(model);
  const epsilon = 0.001;
  const isInternal =
    measure?.low &&
    measure?.high &&
    (measure.low[0] || 0) >= epsilon &&
    (measure.high[0] || 0) <= size.x - epsilon &&
    (measure.low[1] || 0) >= epsilon &&
    (measure.high[1] || 0) <= size.y - epsilon;

  const hash = `${dx},${dy},${cutout.orientation || 0},${cutout.path},${inv?.x ? "x" : ""}${inv?.y ? "y" : ""}`;

  return { cutout, model, isInternal: !!isInternal, hash, dx, dy };
}

/**
 * Cache for boundary shapes (outer panel shape after edge cutouts).
 *
 * Bounded, unlike the hole cache below: its key carries the panel size, so a
 * resize drag walks it through a new entry every frame and an unbounded map
 * would grow for as long as the session lasts — under a geometry cache that
 * takes care to evict.
 */
const BOUNDARY_SHAPE_CACHE_LIMIT = 64;
const boundaryShapeCache = new Map<string, THREE.Shape>();

// Cache for hole shapes by SVG path + orientation (position-independent)
const holeShapeCache = new Map<string, THREE.Shape[]>();

/** Generate cache key for boundary (panel size + edge cutouts) */
function generateBoundaryKey(
  size: THREE.Vector2,
  edgeCutouts: TransformedCutout[],
): string {
  const sizeKey = `${size.x},${size.y}`;
  const cutoutsKey = edgeCutouts.map((c) => c.hash).join("|");
  return `${sizeKey}::${cutoutsKey}`;
}

/** Get or compute hole shapes for an SVG path at origin (cached by path+orientation+invert) */
function getCachedHoleShapes(
  svgPath: string,
  orientation: number,
  invert?: { x?: boolean; y?: boolean },
): THREE.Shape[] {
  const cacheKey = `${svgPath}|${orientation}|${invert?.x ? "x" : ""}|${invert?.y ? "y" : ""}`;
  let shapes = holeShapeCache.get(cacheKey);

  if (!shapes) {
    // Compute shape at origin (legacy invert before SVG→panel mirror/rotate)
    let cachedModel = getCachedSvgModel(svgPath);
    if (invert?.x) {
      cachedModel = MakerJS.model.mirror(cachedModel, true, false);
    }
    if (invert?.y) {
      cachedModel = MakerJS.model.mirror(cachedModel, false, true);
    }
    const model = MakerJS.model.rotate(
      MakerJS.model.scale(
        MakerJS.model.mirror(cachedModel, false, true),
        1,
        true,
      ),
      orientation,
      [0, 0],
    );
    shapes = convertChainsToHoles(model);
    holeShapeCache.set(cacheKey, shapes);
  }

  return shapes;
}

/** Translate a shape by dx, dy */
function translateShape(
  shape: THREE.Shape,
  dx: number,
  dy: number,
): THREE.Shape {
  const points = shape.getPoints();
  const translatedPoints = points.map(
    (p) => new THREE.Vector2(p.x + dx, p.y + dy),
  );
  return new THREE.Shape(translatedPoints);
}

/** Compute boundary shape from edge cutouts (expensive - boolean ops) */
function computeBoundaryShape(
  size: THREE.Vector2,
  edgeCutouts: TransformedCutout[],
): THREE.Shape {
  let boundaryModel: MakerJS.IModel = new MakerJS.models.Rectangle(
    size.x,
    size.y,
  );

  for (const { model } of edgeCutouts) {
    try {
      const nextModel = MakerJS.model.combineSubtraction(boundaryModel, model);
      const candidateShape = ThreeToolsInstance.MakerJSModel2Shape(nextModel);
      if (candidateShape.getPoints().length >= 3) {
        boundaryModel = nextModel;
      } else {
        console.warn(
          "Edge cutout subtraction produced invalid model, skipping",
        );
      }
    } catch (error) {
      console.warn("Fehler beim Verarbeiten eines Rand-Cutouts:", error);
    }
  }

  const shape = ThreeToolsInstance.MakerJSModel2Shape(boundaryModel);

  if (shape.getPoints().length < 3) {
    return new THREE.Shape([
      new THREE.Vector2(0, 0),
      new THREE.Vector2(size.x, 0),
      new THREE.Vector2(size.x, size.y),
      new THREE.Vector2(0, size.y),
    ]);
  }

  // Ensure winding order is CCW
  if (THREE.ShapeUtils.isClockWise(shape.getPoints())) {
    const points = shape.getPoints().reverse();
    shape.curves = new THREE.Shape(points).curves;
  }

  return shape;
}

/** Compute internal holes from internal cutouts (uses shape cache + translation) */
function computeInternalHoles(
  internalCutouts: TransformedCutout[],
): THREE.Shape[] {
  const holes: THREE.Shape[] = [];
  for (const { cutout, dx, dy } of internalCutouts) {
    try {
      if (!cutout.path || typeof cutout.path !== "string") continue;
      const cachedShapes = getCachedHoleShapes(
        cutout.path,
        cutout.orientation || 0,
        (cutout as { invert?: { x?: boolean; y?: boolean } }).invert,
      );
      // Translate cached shapes to actual position
      for (const shape of cachedShapes) {
        holes.push(translateShape(shape, dx, dy));
      }
    } catch (error) {
      console.warn("Fehler beim Verarbeiten eines internen Cutouts:", error);
    }
  }
  return holes;
}

/**
 * Creates the panel geometry with cutouts using Three.js holes.
 * Caches boundary shape and internal holes separately for performance.
 */
export function createPanelGeometryWithHoles(
  size: THREE.Vector2,
  position: THREE.Vector3,
  side: Sides,
  cutouts: ExtendedCutoutDefinition[],
  strength: number,
): THREE.BufferGeometry {
  const rectProps = RectPropertiesBySide[side];
  if (!rectProps) {
    return createPlainPanelGeometry(size, strength);
  }

  try {
    // Transform and classify all cutouts
    const transformed: TransformedCutout[] = [];
    for (const cutout of cutouts) {
      const result = transformAndClassifyCutout(
        cutout,
        size,
        position,
        rectProps,
      );
      if (result) transformed.push(result);
    }

    // Separate edge-touching and internal cutouts
    const edgeCutouts = transformed.filter((c) => !c.isInternal);
    const internalCutouts = transformed.filter((c) => c.isInternal);

    // Get or compute boundary shape (cached by edge cutouts)
    const boundaryKey = generateBoundaryKey(size, edgeCutouts);
    let boundaryShape = boundaryShapeCache.get(boundaryKey);
    if (!boundaryShape) {
      boundaryShape = computeBoundaryShape(size, edgeCutouts);
      if (boundaryShapeCache.size >= BOUNDARY_SHAPE_CACHE_LIMIT) {
        // Map iterates in insertion order, so this is the oldest entry.
        const oldest = boundaryShapeCache.keys().next().value;
        if (oldest !== undefined) boundaryShapeCache.delete(oldest);
      }
      boundaryShapeCache.set(boundaryKey, boundaryShape);
    }

    // Compute internal holes (shape computation is cached, only translation happens here)
    const internalHoles = computeInternalHoles(internalCutouts);

    // Clone boundary shape and add holes
    const panelShape = boundaryShape.clone();
    panelShape.holes.push(...internalHoles);

    return createExtrudedGeometry(panelShape, size, strength);
  } catch (error) {
    console.warn("Fehler bei der Extrusion:", error);
    return createPlainPanelGeometry(size, strength);
  }
}

export const PanelRenderer = memo(({ id }: { id: string }) => {
  // Verwende usePartDefinition nur für gemeinsame Properties
  // color und material werden direkt aus fullPartDef gelesen
  const partDef = usePartDefinition<
    PanelDefinition &
      TrayPanelDefinition & {
        strength: number;
        side: Sides;
        orientation?: number;
      }
  >({
    id,
    select: [
      "size",
      "position",
      "rotation",
      "cutouts",
      "strength",
      "side",
      "orientation",
    ] as const,
  });

  // Filtere doppelte Cutouts - muss vor early return sein
  const { uniqueCutouts } = useMemo(
    () => filterDuplicateCutouts(partDef?.cutouts ?? []),
    [partDef?.cutouts],
  );

  // Extrahiere color und strength direkt aus dem Store - sollten immer gesetzt sein
  const useProductStore = useProductStoreContext();
  const fullPartDef = useProductStore((state) =>
    state.product?.partDefinitions.get(id),
  );

  // Color: Immer gesetzt (Fallback zu Default in BuildStep)
  // Fallback nur für Edge-Cases - eigentlich sollte color immer gesetzt sein
  const fallbackColor = { color: "#808080", metalic: undefined } as PanelColor;
  const color: PanelColor =
    fullPartDef && "color" in fullPartDef && fullPartDef.color
      ? (fullPartDef.color as PanelColor)
      : fallbackColor;

  // Strength: Immer gesetzt in fullPartDef (wird im BuildStep gesetzt)
  const strength =
    fullPartDef &&
    "strength" in fullPartDef &&
    typeof fullPartDef.strength === "number"
      ? fullPartDef.strength
      : 5;

  const panelFields = readPanelGeometryFields(fullPartDef);
  const geometryKey = panelFields
    ? panelGeometryKey(
        panelFields.size,
        panelFields.position,
        panelFields.side,
        uniqueCutouts,
        strength,
      )
    : null;
  const panelGeometry = useLatestCommittedResource(geometryKey, () => {
    // A non-null key guarantees the placement fields exist.
    if (!panelFields || geometryKey === null) {
      throw new Error("Missing panel geometry inputs");
    }
    const { size, position, side } = panelFields;
    return panelGeometryCache.acquire(geometryKey, () =>
      uniqueCutouts.length === 0
        ? createPlainPanelGeometry(size, strength)
        : createPanelGeometryWithHoles(
            size,
            position,
            side,
            uniqueCutouts,
            strength,
          ),
    );
  });
  const meshRef = useRef<THREE.Mesh>(null);
  useLayoutEffect(() => {
    if (panelGeometry && meshRef.current) {
      markProductSceneChanged(meshRef.current);
    }
  }, [panelGeometry]);

  // Early return wenn Geometrie nicht erstellt werden konnte
  // (panelGeometry ist bereits null wenn partDef-Daten fehlen)
  if (!panelGeometry) return null;

  // Get orientation from part definition (for UnderlayPanels)
  // Orientation is in degrees, convert to radians for Three.js
  const orientationDeg = partDef?.orientation ?? 0;
  const orientationRad = (orientationDeg * Math.PI) / 180;

  // React Three Fiber props that Biome doesn't recognize - these are valid props
  const meshProps = {
    rotation: [Math.PI / 2, 0, orientationRad] as [number, number, number],
  };
  // Get panel size for texture scaling
  const panelSize = panelFields?.size;

  // Only apply leather texture to outer panels (from OuterPanelBuildStep)
  // Other panels like TrayPanels, RollerBoards, etc. should not have the texture
  const isOuterPanel = fullPartDef?.buildstepName === "OuterPanelBuildStep";

  return (
    <group>
      {/* Base rotation matches standard: rotate 90° around X so Z becomes Y */}
      {/* Additional orientation rotation from buildstep (for UnderlayPanels) */}
      <mesh ref={meshRef} geometry={panelGeometry} {...meshProps}>
        <PanelMaterial
          color={color}
          panelSize={panelSize}
          textured={isOuterPanel}
        />
        {/* Black outlines — bound to the "edge lines" toggle, as on every
            other part. Left on permanently, the contour is the strongest
            "this is a drawing" cue in the image. */}
        <PartEdges />
      </mesh>
    </group>
  );
});

PanelRenderer.displayName = "PanelRenderer";
